/**
 * Account Domain Types - Multi-Tenant System
 *
 * Defines types for account/workspace management, subscriptions, and tenant isolation.
 */

import type { TypedId } from './idPrefixes';

// =============================================================================
// ID TYPES
// =============================================================================

/** Account ID type (workspace/tenant) */
export type AccountId = TypedId<'acc'>;

/** Domain ID type */
export type DomainId = TypedId<'dom'>;

/** Subscription ID type */
export type SubscriptionId = TypedId<'sub'>;

/** Invitation ID type */
export type InvitationId = TypedId<'inv'>;

// =============================================================================
// ENUMS / CONSTANTS
// =============================================================================

/** Account status values */
export const ACCOUNT_STATUS = {
  TRIAL: 'trial',
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  CANCELLED: 'cancelled',
  PENDING: 'pending',
} as const;

export type AccountStatus = (typeof ACCOUNT_STATUS)[keyof typeof ACCOUNT_STATUS];

/** Subscription plan values */
export const SUBSCRIPTION_PLANS = {
  STARTER: 'starter',
  PROFESSIONAL: 'professional',
  ENTERPRISE: 'enterprise',
  CUSTOM: 'custom',
} as const;

export type SubscriptionPlan = (typeof SUBSCRIPTION_PLANS)[keyof typeof SUBSCRIPTION_PLANS];

/** Account member roles */
export const MEMBER_ROLES = {
  OWNER: 'owner',
  ADMIN: 'admin',
  MEMBER: 'member',
  VIEWER: 'viewer',
} as const;

export type MemberRole = (typeof MEMBER_ROLES)[keyof typeof MEMBER_ROLES];

/** Subscription status values */
export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  PAST_DUE: 'past_due',
  CANCELLED: 'cancelled',
  TRIALING: 'trialing',
  PAUSED: 'paused',
} as const;

export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUS)[keyof typeof SUBSCRIPTION_STATUS];

/** Domain type values */
export const DOMAIN_TYPES = {
  SUBDOMAIN: 'subdomain',
  CUSTOM: 'custom',
} as const;

export type DomainType = (typeof DOMAIN_TYPES)[keyof typeof DOMAIN_TYPES];

/** SSL status values */
export const SSL_STATUS = {
  PENDING: 'pending',
  ACTIVE: 'active',
  ERROR: 'error',
  NOT_REQUIRED: 'not_required',
} as const;

export type SslStatus = (typeof SSL_STATUS)[keyof typeof SSL_STATUS];

/** Invitation status values */
export const INVITATION_STATUS = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  EXPIRED: 'expired',
  REVOKED: 'revoked',
} as const;

export type InvitationStatus = (typeof INVITATION_STATUS)[keyof typeof INVITATION_STATUS];

/** Billing interval values */
export const BILLING_INTERVALS = {
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
} as const;

export type BillingInterval = (typeof BILLING_INTERVALS)[keyof typeof BILLING_INTERVALS];

// =============================================================================
// PLAN LIMITS
// =============================================================================

/** Storage quota per plan (in bytes) */
export const PLAN_STORAGE_QUOTA: Record<SubscriptionPlan, number> = {
  starter: 1 * 1024 * 1024 * 1024, // 1 GB
  professional: 10 * 1024 * 1024 * 1024, // 10 GB
  enterprise: 100 * 1024 * 1024 * 1024, // 100 GB
  custom: 0, // Defined per-account
};

/** Max users per plan */
export const PLAN_MAX_USERS: Record<SubscriptionPlan, number> = {
  starter: 3,
  professional: 10,
  enterprise: 50,
  custom: 0, // Defined per-account
};

/** Monthly price per plan (in cents) */
export const PLAN_PRICES: Record<SubscriptionPlan, number> = {
  starter: 0, // Free
  professional: 2900, // 29.00 EUR
  enterprise: 9900, // 99.00 EUR
  custom: 0, // Custom pricing
};

// =============================================================================
// CORE INTERFACES
// =============================================================================

/**
 * Account - Tenant/Workspace
 *
 * Represents a multi-tenant workspace with its own isolated data schema and storage.
 */
export interface Account {
  id: AccountId;
  name: string;
  slug: string; // URL-safe slug for subdomain: {slug}.ba.teknology.eu
  schemaName: string; // PostgreSQL schema name: acc_xxxxx

  // Owner
  ownerId: string; // usr-xxx

  // Status
  status: AccountStatus;
  plan: SubscriptionPlan;

  // Storage
  storageQuotaBytes: number;
  storageUsedBytes: number;

  // User limits
  maxUsers: number;
  currentUsers: number;

  // Billing
  billingEmail: string;

  // Custom domain (optional)
  customDomain?: string;

  // Trial/Expiration
  trialEndsAt?: string;
  expiresAt?: string;

  // Settings (JSON)
  settings?: AccountSettings;

  // Timestamps
  createdAt: string;
  updatedAt: string;
  suspendedAt?: string;
  cancelledAt?: string;
}

/**
 * Account settings stored as JSON
 */
export interface AccountSettings {
  companyName?: string;
  companyLogo?: string;
  companyAddress?: string;
  invoicePrefix?: string;
  invoiceNextNumber?: number;
  defaultCurrency?: string;
  defaultTaxRate?: number;
  defaultPaymentTerms?: number;
  emailSignature?: string;
  timezone?: string;
  features?: AccountFeatures;
}

/**
 * Feature flags for account
 */
export interface AccountFeatures {
  aiAssistant?: boolean;
  eInvoicing?: boolean;
  timeTracking?: boolean;
  bankSync?: boolean;
  customDomain?: boolean;
  apiAccess?: boolean;
  webhooks?: boolean;
  whiteLabel?: boolean;
}

/**
 * Account member - Junction between User and Account
 */
export interface AccountMember {
  accountId: AccountId;
  userId: string; // usr-xxx
  role: MemberRole;
  permissions?: MemberPermissions;
  isActive: boolean;
  invitedBy?: string; // usr-xxx
  invitedAt: string;
  joinedAt?: string;
}

/**
 * Member-specific permissions (overrides role defaults)
 */
export interface MemberPermissions {
  projects?: 'none' | 'view' | 'edit' | 'admin';
  calendar?: 'none' | 'view' | 'edit';
  hub?: 'none' | 'view' | 'edit';
  accounting?: 'none' | 'view' | 'edit' | 'admin';
  settings?: boolean;
}

/**
 * Account domain - Subdomain or custom domain mapping
 */
export interface AccountDomain {
  id: DomainId;
  accountId: AccountId;
  domain: string; // e.g., "cliente.ba.teknology.eu" or "app.cliente.com"
  domainType: DomainType;
  isPrimary: boolean;
  sslStatus: SslStatus;
  sslExpiresAt?: string;
  verificationToken?: string;
  verified: boolean;
  verifiedAt?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Subscription - Billing and subscription management
 */
export interface Subscription {
  id: SubscriptionId;
  accountId: AccountId;
  plan: SubscriptionPlan;
  status: SubscriptionStatus;

  // Billing period
  currentPeriodStart?: string;
  currentPeriodEnd?: string;

  // Pricing
  monthlyPriceCents?: number;
  currency: string;
  billingInterval: BillingInterval;

  // Stripe integration (optional)
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  stripePriceId?: string;

  // Trial
  trialStart?: string;
  trialEnd?: string;

  // Cancellation
  cancelAtPeriodEnd: boolean;
  cancelledAt?: string;
  cancellationReason?: string;

  // Timestamps
  createdAt: string;
  updatedAt: string;
}

/**
 * Account invitation - Pending invitation to join an account
 */
export interface AccountInvitation {
  id: InvitationId;
  accountId: AccountId;
  email: string;
  role: MemberRole;
  token: string;
  status: InvitationStatus;
  invitedBy: string; // usr-xxx
  createdAt: string;
  expiresAt: string;
  acceptedAt?: string;
}

// =============================================================================
// VIEW TYPES (Aggregated data)
// =============================================================================

/**
 * Account overview - Extended account info with related data
 */
export interface AccountOverview extends Account {
  ownerName?: string;
  ownerEmail?: string;
  primaryDomain?: string;
  subscriptionPlan?: SubscriptionPlan;
  subscriptionStatus?: SubscriptionStatus;
  subscriptionEndsAt?: string;
  storageUsagePercent?: number;
}

/**
 * Account with members list
 */
export interface AccountWithMembers extends Account {
  members: AccountMemberWithUser[];
}

/**
 * Account member with user details
 */
export interface AccountMemberWithUser extends AccountMember {
  user: {
    id: string;
    email: string;
    displayName: string;
    avatarUrl?: string;
  };
}

// =============================================================================
// FORM/INPUT TYPES
// =============================================================================

/**
 * Input for creating a new account
 */
export interface CreateAccountInput {
  name: string;
  slug: string;
  billingEmail: string;
  plan?: SubscriptionPlan;
  ownerId?: string; // If not provided, current user becomes owner
}

/**
 * Input for updating an account
 */
export interface UpdateAccountInput {
  name?: string;
  billingEmail?: string;
  customDomain?: string;
  settings?: Partial<AccountSettings>;
}

/**
 * Input for inviting a member
 */
export interface InviteMemberInput {
  email: string;
  role: MemberRole;
}

/**
 * Input for updating a member
 */
export interface UpdateMemberInput {
  role?: MemberRole;
  permissions?: MemberPermissions;
  isActive?: boolean;
}

/**
 * Input for adding a custom domain
 */
export interface AddDomainInput {
  domain: string;
  isPrimary?: boolean;
}

// =============================================================================
// UTILITY TYPES
// =============================================================================

/**
 * Storage usage info
 */
export interface StorageUsage {
  usedBytes: number;
  quotaBytes: number;
  usedPercent: number;
  availableBytes: number;
  isNearLimit: boolean; // > 80%
  isAtLimit: boolean; // >= 100%
}

/**
 * Account limits info
 */
export interface AccountLimits {
  storage: StorageUsage;
  users: {
    current: number;
    max: number;
    available: number;
    isAtLimit: boolean;
  };
}

/**
 * Slug validation result
 */
export interface SlugValidation {
  isValid: boolean;
  isAvailable: boolean;
  error?: 'invalid_format' | 'too_short' | 'too_long' | 'reserved' | 'taken';
  suggestion?: string;
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Calculate storage usage from account data
 */
export function calculateStorageUsage(account: Account): StorageUsage {
  const usedBytes = account.storageUsedBytes;
  const quotaBytes = account.storageQuotaBytes;
  const usedPercent = quotaBytes > 0 ? (usedBytes / quotaBytes) * 100 : 0;
  const availableBytes = Math.max(0, quotaBytes - usedBytes);

  return {
    usedBytes,
    quotaBytes,
    usedPercent: Math.round(usedPercent * 100) / 100,
    availableBytes,
    isNearLimit: usedPercent >= 80,
    isAtLimit: usedPercent >= 100,
  };
}

/**
 * Format bytes to human-readable string
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Validate slug format
 */
export function validateSlugFormat(slug: string): {
  valid: boolean;
  error?: string;
} {
  if (slug.length < 3) {
    return { valid: false, error: 'Slug must be at least 3 characters' };
  }
  if (slug.length > 50) {
    return { valid: false, error: 'Slug must be at most 50 characters' };
  }
  if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(slug)) {
    return {
      valid: false,
      error: 'Slug must start and end with a letter or number, and contain only lowercase letters, numbers, and hyphens',
    };
  }
  if (/--/.test(slug)) {
    return { valid: false, error: 'Slug cannot contain consecutive hyphens' };
  }
  return { valid: true };
}

/**
 * Generate slug from name
 */
export function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[àáâãäå]/g, 'a')
    .replace(/[èéêë]/g, 'e')
    .replace(/[ìíîï]/g, 'i')
    .replace(/[òóôõö]/g, 'o')
    .replace(/[ùúûü]/g, 'u')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
}

/**
 * Check if user has permission for action in account
 */
export function hasAccountPermission(
  member: AccountMember,
  action: 'view' | 'edit' | 'admin' | 'delete',
): boolean {
  const rolePermissions: Record<MemberRole, string[]> = {
    owner: ['view', 'edit', 'admin', 'delete'],
    admin: ['view', 'edit', 'admin'],
    member: ['view', 'edit'],
    viewer: ['view'],
  };

  return rolePermissions[member.role]?.includes(action) ?? false;
}

/**
 * Get plan display name
 */
export function getPlanDisplayName(plan: SubscriptionPlan): string {
  const names: Record<SubscriptionPlan, string> = {
    starter: 'Starter',
    professional: 'Professional',
    enterprise: 'Enterprise',
    custom: 'Custom',
  };
  return names[plan];
}

/**
 * Get status display info
 */
export function getStatusInfo(status: AccountStatus): {
  label: string;
  color: 'green' | 'yellow' | 'red' | 'gray';
} {
  const info: Record<AccountStatus, { label: string; color: 'green' | 'yellow' | 'red' | 'gray' }> =
    {
      trial: { label: 'Trial', color: 'yellow' },
      active: { label: 'Active', color: 'green' },
      suspended: { label: 'Suspended', color: 'red' },
      cancelled: { label: 'Cancelled', color: 'gray' },
      pending: { label: 'Pending', color: 'yellow' },
    };
  return info[status];
}
